import { useState, useEffect, Dispatch, SetStateAction } from 'react';

/**
 * A useState drop-in that persists to sessionStorage under `key`, so a list's
 * search / filter survives navigating to a detail and back: the list component
 * unmounts on navigate and remounts on return, but reads back its last value
 * instead of resetting to empty. This is the fix for the "search, click a
 * result, go back, the search is gone, re-type it" frustration across the
 * admin panel.
 *
 * sessionStorage (not local): per-tab and cleared when the tab closes — the
 * right lifetime for "don't make me re-type what I just searched" without
 * leaking filters into a fresh session. Fully guarded: storage can be blocked
 * (private windows) or throw, so every read/write is try/caught and falls back
 * to `initial`, and the hook always behaves like plain useState if storage is
 * unavailable.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  // An empty key means "don't persist" — behave exactly like plain useState. This
  // lets a shared component (e.g. DataTable) opt in only when a storageKey is
  // passed, without collisions when it isn't.
  const persist = !!key;
  const [val, setVal] = useState<T>(() => {
    if (!persist) return initial;
    try {
      const raw = sessionStorage.getItem(key);
      return raw != null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    if (!persist) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(val));
    } catch {
      /* storage unavailable — behave like plain useState */
    }
  }, [key, persist, val]);
  return [val, setVal];
}
